import { Alert, Button, Form, FormInstance, Input, Radio, Upload } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import clsx from 'clsx';
import {
  MAX_UPLOAD_FILESIZE,
  MAX_UPLOAD_FILESIZE_BYTES,
} from 'const/attachments';
import { getFieldRules } from 'const/formRules';
import usePickList from 'hooks/usePickList';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PickListValue } from 'types/PickList';
import { getValueFromEvent } from 'utils/files';
import AddressFormFields, {
  AddressFormFieldsConfig,
} from 'views/dossiers/concessionnaire/steps/components/AddressFormFields';

export interface BeneficiaryFormType {
  same_contact?: 'oui' | 'non';
  civilite: string;
  firstname: string;
  lastname: string;
  email: string;
  descriptionProjet: string;
  telephone: string;
  modeleIncitationFinanciere: string;
  own_company?: 'oui' | 'non';
  raisonSocialeETX?: string;
  adresseETX?: string;
  codePostalETX?: string;
  villeETX?: string;
  telephoneETX?: string;
  emailETX?: string;
  files?: UploadFile[];
}

export interface SiteContactInfo {
  lastname: string;
  firstname: string;
  email: string;
}

interface BeneficiaryFormProps extends FormProps<BeneficiaryFormType> {
  formInstance: FormInstance<BeneficiaryFormType>;
  siteContactInfo?: SiteContactInfo;
}

export default function BeneficiaryForm({
  formInstance,
  siteContactInfo,
  ...props
}: BeneficiaryFormProps) {
  const { t } = useTranslation('add_dossier_beneficiary');

  const [fileSizeErrors, setFileSizeErrors] = useState<string[]>([]);

  const { data: aifModels } = usePickList(
    'dossiers',
    'modeleIncitationFinanciere',
  );

  const aifModel = Form.useWatch('modeleIncitationFinanciere', formInstance);
  const ownCompany = Form.useWatch('own_company', formInstance);
  const sameContact = Form.useWatch('same_contact', formInstance);
  const etxAddressConfig = useMemo<AddressFormFieldsConfig>(
    () => ({
      fieldNames: {
        address: 'adresseETX',
        postalCode: 'codePostalETX',
        city: 'villeETX',
      },
      labels: {
        address: t('adresseETX'),
        postalCode: t('codePostalETX'),
        city: t('villeETX'),
      },
      placeholders: {
        address: t('adresseETX_placeholder'),
        postalCode: t('codePostalETX_placeholder'),
        city: t('villeETX_placeholder'),
      },
      required: {
        address: false,
        postalCode: false,
        city: false,
      },
      validateDepartmentCode: false,
      queryKey: 'beneficiary-etx-addresses',
    }),
    [t],
  );

  const handleSameContactChange = (value: 'oui' | 'non') => {
    if (value === 'oui' && siteContactInfo) {
      formInstance.setFieldsValue({
        firstname: siteContactInfo.firstname,
        lastname: siteContactInfo.lastname,
        email: siteContactInfo.email,
      });
    }
    if (value === 'non') {
      formInstance.setFieldsValue({
        firstname: undefined,
        lastname: undefined,
        email: undefined,
      });
    }
  };

  useEffect(() => {
    return () => {
      setFileSizeErrors([]);
    };
  }, []);

  const beforeUpload = useCallback(
    (file: RcFile) => {
      const isTooBig = file.size > MAX_UPLOAD_FILESIZE_BYTES;

      if (isTooBig) {
        const errorMessage = t('attachments:file_size_error', {
          filename: file.name,
          maxSize: MAX_UPLOAD_FILESIZE,
        });

        setFileSizeErrors((prev) => [
          ...prev.filter((err) => !err.includes(file.name)),
          errorMessage,
        ]);

        // Prevent oversized files from being added to the list
        return Upload.LIST_IGNORE;
      }

      setFileSizeErrors((prev) =>
        prev.filter((err) => !err.includes(file.name)),
      );
      return false;
    },
    [t],
  );

  return (
    <Form<BeneficiaryFormType> form={formInstance} layout="vertical" {...props}>
      <Form.Item<BeneficiaryFormType>
        name="same_contact"
        label={t('same_contact')}
        required
      >
        <Radio.Group onChange={(e) => handleSameContactChange(e.target.value)}>
          <Radio value="oui">{t('global:yes')}</Radio>
          <Radio value="non"> {t('global:no')} </Radio>
        </Radio.Group>
      </Form.Item>
      <div className={clsx({ hidden: sameContact !== 'non' })}>
        <Form.Item<BeneficiaryFormType>
          name="civilite"
          required={sameContact === 'non'}
          label={t('civilite')}
        >
          <Radio.Group>
            <Radio value="M.">{t('global:mr')}</Radio>
            <Radio value="Mme"> {t('global:mrs')} </Radio>
          </Radio.Group>
        </Form.Item>
        {(['firstname', 'lastname', 'email', 'telephone'] as const).map(
          (item) => (
            <Form.Item<BeneficiaryFormType>
              key={item}
              name={item}
              label={t(item)}
              required={sameContact === 'non'}
              rules={getFieldRules(t, {
                required: sameContact === 'non',
                email: item === 'email',
                phone: item === 'telephone',
                text_only: item === 'firstname' || item === 'lastname',
              })}
            >
              <Input placeholder={t(`${item}_placeholder`)} />
            </Form.Item>
          ),
        )}
      </div>
      <Form.Item<BeneficiaryFormType>
        name="modeleIncitationFinanciere"
        label={t('aif_model')}
        extra={
          <Alert
            showIcon
            className="py-2 mt-4"
            type="info"
            description={
              <p className="text-bleu-hellio whitespace-pre-line">
                {t('aif_alert')}
              </p>
            }
          />
        }
        required
      >
        <Radio.Group>
          {aifModels?.map(({ label, value }: PickListValue) => (
            <Radio key={value} value={value}>
              {label}
            </Radio>
          ))}
          <Radio key="undefined" value="">
            {t('aif_model_undefined')}
          </Radio>
        </Radio.Group>
      </Form.Item>
      {aifModel === 'Remise sur devis' && (
        <>
          <Form.Item<BeneficiaryFormType>
            required
            name="own_company"
            label={t('own_company')}
          >
            <Radio.Group>
              <Radio value="oui">{t('global:yes')}</Radio>
              <Radio value="non"> {t('global:no')} </Radio>
            </Radio.Group>
          </Form.Item>
          {ownCompany === 'non' && (
            <>
              <Form.Item<BeneficiaryFormType>
                name="raisonSocialeETX"
                label={t('raisonSocialeETX')}
                required
                rules={getFieldRules(t, { required: true })}
              >
                <Input placeholder={t('raisonSocialeETX_placeholder')} />
              </Form.Item>
              <AddressFormFields config={etxAddressConfig} />
              {(['telephoneETX', 'emailETX'] as const).map((item) => (
                <Form.Item<BeneficiaryFormType>
                  key={item}
                  name={item}
                  label={t(item)}
                  required
                  rules={getFieldRules(t, {
                    required: true,
                    email: item === 'emailETX',
                    phone: item === 'telephoneETX',
                  })}
                >
                  <Input placeholder={t(`${item}_placeholder`)} />
                </Form.Item>
              ))}
            </>
          )}
        </>
      )}
      <Form.Item<BeneficiaryFormType>
        name="descriptionProjet"
        label={t('descriptionProjet')}
        required
      >
        <Input.TextArea
          rows={4}
          placeholder={t('descriptionProjet_placeholder')}
        />
      </Form.Item>
      <div className="mb-6">
        <p className="text-sm font-medium mb-2">{t('files_title')}</p>
        <div className="flex flex-col gap-2">
          <Form.Item<BeneficiaryFormType>
            name="files"
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
            className="mb-0"
          >
            <Upload
              multiple
              maxCount={10}
              className="[&>.ant-upload]:w-full"
              beforeUpload={beforeUpload}
              customRequest={({ onSuccess }) => {
                if (onSuccess) onSuccess('ok');
              }}
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            >
              <div className="flex items-center gap-4 border border-dashed border-bleu-hellio-500 rounded-lg px-4 py-3">
                <Button
                  shape="round"
                  className="border-bleu-hellio-500 text-bleu-hellio-500"
                >
                  {t('files_browse')}
                </Button>
                <span className="text-sm text-gray-400">
                  {t('files_max_size', { size: MAX_UPLOAD_FILESIZE })}
                </span>
              </div>
            </Upload>
          </Form.Item>

          {fileSizeErrors.length > 0 && (
            <div className="flex flex-col gap-2">
              {fileSizeErrors.map((error, index) => (
                <Alert
                  key={index}
                  showIcon
                  message={error}
                  type="error"
                  className="bg-transparent border-none text-rouge-copro-10 text-left px-0"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Form>
  );
}
